import React, { useState } from "react";
import { ArrowRight, BookOpen, Filter, Github } from "lucide-react";
import { PRODUCTS } from "../data/products";
import WaitlistModal from "../components/WaitlistModal";

const CATEGORY_COLORS = {
  "memory": "#15803D",
  "evals": "#7E22CE",
  "mcp-tools": "#BE185D",
};

const FILTERS = ["memory", "evals", "mcp-tools"];
const FILTER_LABELS = {
  "memory": "MEMORY", "evals": "EVALS", "mcp-tools": "MCP TOOLS",
};

export default function Products() {
  const [filter, setFilter] = useState("All");
  const [waitlistProduct, setWaitlistProduct] = useState(null);
  const filtered = filter === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === filter);
  const loading = false;

  const handleCardClick = (product) => (e) => {
    // Don't intercept clicks on inner interactive elements (buttons, links)
    if (e.target.closest("a, button")) return;
    if (product.docs_url) {
      window.location.href = product.docs_url;
    } else if (product.status === "coming-soon") {
      setWaitlistProduct(product);
    }
  };

  return (
    <div data-testid="products-page" className="bg-[#F5F5F0] min-h-screen pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="border-b border-[#D4D4D4] pb-12 mb-12">
          <p className="section-tag mb-3">&gt; PRODUCTS.EXE</p>
          <h1 className="font-pixel text-3xl md:text-5xl text-[#0A0A0A] leading-tight mb-4">
            AI ARSENAL
          </h1>
          <p className="font-terminal text-sm text-[#0A0A0A]/50 max-w-2xl leading-relaxed">
            Production-grade AI systems for voice, chat, and autonomous workflows. Every product ships battle-tested and ready to scale from day one.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-10">
          <Filter size={14} className="text-[#0A0A0A]/30 self-center mr-2" />
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(filter === f ? "All" : f)}
              data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-arcade text-base uppercase tracking-wide px-4 py-2 border transition-colors duration-150 ${
                filter === f
                  ? "bg-[#4ADE80] text-black border-[#4ADE80]"
                  : "bg-transparent text-[#0A0A0A]/50 border-[#C8C8C8] hover:border-[#4ADE80] hover:text-[#15803D]"
              }`}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex flex-col gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white border-2 border-[#E5E5E5] h-72 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {filtered.map((product, i) => (
              <div
                key={product.id}
                id={product.id}
                data-testid={`product-${product.id}`}
                onClick={handleCardClick(product)}
                className="group bg-white border-2 border-[#D4D4D4] card-hover overflow-hidden flex flex-col md:flex-row scroll-mt-24 cursor-pointer"
              >
                {/* Image */}
                <div className="relative w-full md:w-[560px] h-56 md:h-auto flex-shrink-0 bg-[#F5F5F0] md:border-r-2 md:border-b-0 border-b-2 border-[#15803D]/15 overflow-hidden flex items-center justify-center aspect-[2/1] md:aspect-auto">
                  <img
                    src={product.image_url}
                    alt={product.display_name}
                    className="w-full h-full object-cover [image-rendering:pixelated]"
                  />
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <span
                      className="font-arcade text-base uppercase border px-2 py-1"
                      style={{
                        color: CATEGORY_COLORS[product.category] || "#4ADE80",
                        borderColor: CATEGORY_COLORS[product.category] || "#4ADE80",
                      }}
                    >
                      [{product.category.replace("-", " ")}]
                    </span>
                    {product.status === "coming-soon" ? (
                      <span className="font-arcade text-sm text-[#BE185D] border border-[#BE185D] px-2 py-1 uppercase">
                        SOON
                      </span>
                    ) : (
                      <span className="font-terminal text-xs text-[#15803D] flex items-center gap-1">
                        <span className="status-online" />LIVE
                      </span>
                    )}
                  </div>

                  <h2 className="font-pixel text-sm text-[#0A0A0A] mb-1">{product.display_name}</h2>
                  <p className="font-arcade text-lg text-[#15803D] mb-3">{product.tagline}</p>
                  <p className="font-terminal text-xs text-[#0A0A0A]/50 leading-relaxed mb-5 flex-1">
                    {product.description}
                  </p>

                  <ul className="space-y-1.5 mb-5">
                    {product.features.map((f, fi) => (
                      <li key={fi} className="font-terminal text-xs text-[#0A0A0A]/60 flex items-start gap-2">
                        <span className="text-[#15803D] mt-0.5">&gt;</span> {f}
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap gap-3 w-fit">
                    {product.docs_url ? (
                      <a
                        href={product.docs_url}
                        data-testid={`docs-${product.id}`}
                        className="inline-flex items-center gap-2 bg-[#4ADE80] text-black font-arcade text-base uppercase px-5 py-2.5 border-2 border-[#4ADE80] hover:bg-transparent hover:text-[#15803D] transition-colors duration-150 btn-press"
                      >
                        <BookOpen size={14} /> DOCUMENTATION
                      </a>
                    ) : product.status === "coming-soon" ? (
                      <button
                        onClick={() => setWaitlistProduct(product)}
                        data-testid={`waitlist-${product.id}`}
                        className="inline-flex items-center gap-2 bg-[#4ADE80] text-black font-arcade text-base uppercase px-5 py-2.5 border-2 border-[#4ADE80] hover:bg-transparent hover:text-[#15803D] transition-colors duration-150 btn-press"
                      >
                        JOIN WAITLIST <ArrowRight size={14} />
                      </button>
                    ) : (
                      <a
                        href={product.links?.pypi || product.links?.github || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`get-started-${product.id}`}
                        className="inline-flex items-center gap-2 bg-[#4ADE80] text-black font-arcade text-base uppercase px-5 py-2.5 border-2 border-[#4ADE80] hover:bg-transparent hover:text-[#15803D] transition-colors duration-150 btn-press"
                      >
                        GET STARTED <ArrowRight size={14} />
                      </a>
                    )}
                    {product.links?.github && (
                      <a
                        href={product.links.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`github-${product.id}`}
                        className="inline-flex items-center gap-2 bg-transparent text-[#0A0A0A] font-arcade text-base uppercase px-5 py-2.5 border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors duration-150 btn-press"
                      >
                        <Github size={14} /> Github
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {filtered.length === 0 && !loading && (
          <p className="font-arcade text-xl text-[#0A0A0A]/30 text-center py-20">
            No products found for this category.
          </p>
        )}
      </div>

      {waitlistProduct && (
        <WaitlistModal
          product={waitlistProduct}
          onClose={() => setWaitlistProduct(null)}
        />
      )}
    </div>
  );
}
